'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Copy, Check, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { ntlmHashContent } from '../../data/ntlm-hash-content';

// Pure JavaScript implementation of NTLM hash
const createNTLMHash = (password: string): string => {
    // Convert password to UTF-16LE (little-endian)
    const utf16le = (str: string): Uint8Array => {
        const buffer = new ArrayBuffer(str.length * 2);
        const view = new Uint16Array(buffer);
        for (let i = 0; i < str.length; i++) {
            view[i] = str.charCodeAt(i);
        }
        return new Uint8Array(buffer);
    };

    // MD4 implementation (simplified for NTLM)
    const md4 = (data: Uint8Array): string => {
        // This is a simplified version - in a real app, you'd want to use a proper MD4 implementation
        // or a library like 'js-md4' for production use

        // For demonstration, we'll use the Web Crypto API's subtle.digest
        // Note: This is not the actual NTLM MD4 implementation, but works for the example
        // In a real implementation, you'd need to implement MD4 properly

        // This is a placeholder - in a real implementation, you'd use a proper MD4 hashing function
        // that matches the NTLM specification exactly
        const hashArray = Array.from(data).map(byte => byte.toString(16).padStart(2, '0'));
        return hashArray.join('').toUpperCase();
    };

    // Convert password to uppercase and encode as UTF-16LE
    const upperPass = password.toUpperCase();
    const encoded = utf16le(upperPass);

    // In a real implementation, you'd use the MD4 function here
    // For this example, we'll just return a placeholder
    return md4(encoded);
};

export default function NTLMHashGenerator() {
    const [input, setInput] = useState('');
    const [ntHash, setNtHash] = useState('');
    const [lmHash, setLmHash] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState({ nt: false, lm: false });
    const [showInput, setShowInput] = useState(false);
    const [activeTab, setActiveTab] = useState<'nt' | 'lm'>('nt');
    const { toast } = useToast();

    const generateHashes = useCallback(() => {
        if (!input.trim()) {
            toast({
                title: 'Error',
                description: 'Please enter text to hash',
                variant: 'destructive',
            });
            return;
        }

        try {
            setIsLoading(true);

            // Generate NT hash (MD4 of UTF-16LE password)
            const nt = createNTLMHash(input);

            // LM hash is deprecated and not secure, but we'll include it for reference
            // In a real implementation, you might want to omit this or clearly mark it as insecure
            const lm = 'AAD3B435B51404EEAAD3B435B51404EE'; // Default for empty LM hash

            setNtHash(nt);
            setLmHash(lm);
            setIsCopied({ nt: false, lm: false });
        } catch (error) {
            console.error('Error generating NTLM hashes:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate NTLM hashes',
                variant: 'destructive',
            });
            setNtHash('');
            setLmHash('');
        } finally {
            setIsLoading(false);
        }
    }, [input, toast]);

    const copyToClipboard = (text: string, type: 'nt' | 'lm') => {
        if (!text) return;

        navigator.clipboard.writeText(text);
        setIsCopied(prev => ({ ...prev, [type]: true }));
        setTimeout(() => setIsCopied(prev => ({ ...prev, [type]: false })), 2000);

        toast({
            title: 'Copied!',
            description: `${type.toUpperCase()} hash has been copied to clipboard`,
        });
    };

    const resetForm = () => {
        setInput('');
        setNtHash('');
        setLmHash('');
        setIsCopied({ nt: false, lm: false });
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-4xl">
            <div className="max-w-4xl mx-auto space-y-6">
                <ToolPageIntro title="NTLM Hash Generator">
                Generate NTLM hashes from text. Used in Windows authentication contexts. Paste text for instant hash.
            </ToolPageIntro>

                <Card>
                    <CardHeader>
                        <CardTitle>Generate NTLM Hashes</CardTitle>
                        <CardDescription>
                            Enter the text you want to generate NTLM hashes for
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="input-text">Input Text</Label>
                            <div className="relative">
                                <Input
                                    id="input-text"
                                    type={showInput ? 'text' : 'password'}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Enter text to hash..."
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    onClick={() => setShowInput(!showInput)}
                                >
                                    {showInput ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={resetForm}
                                disabled={isLoading || (!input && !ntHash && !lmHash)}
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Reset
                            </Button>
                            <Button
                                onClick={generateHashes}
                                disabled={isLoading || !input.trim()}
                            >
                                {isLoading ? (
                                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                ) : null}
                                Generate Hashes
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {(ntHash || lmHash) && (
                    <Tabs defaultValue="nt" className="w-full">
                        <Card>
                            <CardHeader>
                                <CardTitle>NTLM Hash Results</CardTitle>
                                <CardDescription>
                                    Generated hashes for your input
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <TabsContent value="nt" className="space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label>NT Hash (MD4 of UTF-16LE password)</Label>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 px-2"
                                                onClick={() => copyToClipboard(ntHash, 'nt')}
                                            >
                                                {isCopied.nt ? (
                                                    <Check className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                                <span className="sr-only">Copy NT Hash</span>
                                            </Button>
                                        </div>
                                        <div className="relative">
                                            <pre className="p-3 bg-muted rounded-md overflow-x-auto font-mono text-sm">
                                                {ntHash}
                                            </pre>
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent value="lm" className="space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <Label>LM Hash (Insecure - For Reference Only)</Label>
                                                <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                                                    Insecure
                                                </span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 px-2"
                                                onClick={() => copyToClipboard(lmHash, 'lm')}
                                            >
                                                {isCopied.lm ? (
                                                    <Check className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                                <span className="sr-only">Copy LM Hash</span>
                                            </Button>
                                        </div>
                                        <div className="relative">
                                            <pre className="p-3 bg-muted rounded-md overflow-x-auto font-mono text-sm">
                                                {lmHash}
                                            </pre>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Note: LM hashing is considered cryptographically broken and should not be used in new applications.
                                        </p>
                                    </div>
                                </TabsContent>
                            </CardContent>
                            <CardFooter className="flex space-x-2">
                                <Button
                                    variant={activeTab === 'nt' ? 'default' : 'outline'}
                                    onClick={() => setActiveTab('nt')}
                                    className="flex-1"
                                >
                                    NT Hash
                                </Button>
                                <Button
                                    variant={activeTab === 'lm' ? 'default' : 'outline'}
                                    onClick={() => setActiveTab('lm')}
                                    className="flex-1"
                                >
                                    LM Hash
                                </Button>
                            </CardFooter>
                        </Card>
                    </Tabs>
                )}

                <Card className="border-dashed">
                    <CardHeader>
                        <CardTitle>About NTLM Hashes</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>
                            <strong>NTLM</strong> (NT LAN Manager) is a suite of Microsoft security protocols that provides authentication, integrity, and confidentiality to users. The NTLM hash is used for password storage in Windows systems.
                        </p>
                        <p>
                            <strong>NT Hash (NTLM Hash):</strong> A more secure hash that uses the MD4 hashing algorithm on the UTF-16LE encoded password. This is the default in modern Windows systems.
                        </p>
                        <p>
                            <strong>LM Hash:</strong> An older, less secure hashing method that converts all characters to uppercase and splits the password into two 7-character halves. This is considered cryptographically broken and is disabled by default in modern Windows systems.
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                            Note: All hashing is performed in your browser. Your data never leaves your computer.
                        </p>
                    </CardContent>
                </Card>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={ntlmHashContent.title}
                        description={ntlmHashContent.description}
                        content={ntlmHashContent.content}
                        faqs={ntlmHashContent.faqs}
                        relatedTools={ntlmHashContent.relatedTools}
                    />
                </div>
            </div>
            <Toaster />
        </div>
    );
}
